import type { ReactNode } from "react";

import { cx } from "../lib/cx";

export interface ToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Render the switch alone, for a table cell; the label becomes its name. */
  compact?: boolean;
}

/**
 * A switch. `role="switch"` with `aria-checked` rather than a checkbox, because
 * these settings apply immediately rather than on a form submit, and the switch
 * role is what tells a screen-reader user so.
 */
export function Toggle({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  compact = false,
}: ToggleProps): ReactNode {
  const control = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        onChange(!checked);
      }}
      className={cx(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "bg-accent" : "border border-line bg-hover",
      )}
    >
      <span
        aria-hidden="true"
        className={cx(
          "inline-block h-3.5 w-3.5 rounded-full bg-raised shadow transition-transform",
          checked ? "translate-x-4.5" : "translate-x-1",
        )}
      />
    </button>
  );

  if (compact) return control;

  return (
    <div className="flex items-start justify-between gap-6 border-b border-line py-3 last:border-b-0">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {description !== undefined && <p className="mt-0.5 text-sm text-muted">{description}</p>}
      </div>
      <div className="mt-0.5">{control}</div>
    </div>
  );
}
